import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { DataService } from "../../providers/data/data.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { ExportDownloadService } from "../../providers/export-download/export-download.service";
import { PreorderBrandReportService } from "../../providers/preorder-brand-report/preorder-brand-report.service";
import { GET_PUBLISHED_PRICELISTS, GET_PREORDER_BRAND_METRICS } from "./pricelists-page.graphql";
import { GET_ACTIVE_CUSTOMER } from "../product-list/product-list.graphql";
import { ContactFormComponent } from "../../../shared/components/contact-form/contact-form.component";
import {
    ExportFormat,
    SHARE_LINK_ERROR_PREFIX,
    SHARE_LINK_SUCCESS,
} from "../../../shared/types/export-format.types";
import {
    formatLeadTimeWeeksLabel,
    pricingLeadTimeToFormFields,
    resolveEffectiveLeadTimeWeeks,
} from "../../../shared/utils/lead-time-weeks.util";
import {
    SUPPLY_TYPE,
    PRICELIST_TEXT,
    MOV_FILTER_OPTIONS,
    LEAD_TIME_FILTER_OPTIONS,
    LEAD_TIME_FILTER_IDS,
    ITEMS_PER_PAGE_OPTIONS,
    DEFAULT_ITEMS_PER_PAGE,
    DEFAULT_PREORDER_ITEMS_PER_PAGE,
    MAX_VISIBLE_PAGES,
} from "../../../shared/constants/pricelist.constants";
import {
    PublishedPricelist,
    PreOrderBrandMetric,
    PreOrderBrandMetricsResponse,
    GetPublishedPricelistsResponse,
    PreOrderBrandGroup,
    PreOrderFilterChip,
    PreOrderSortKey,
} from "../../../shared/types/pricelist.types";

@Component({
    selector: "vsf-pricelists-page",
    templateUrl: "./pricelists-page.component.html",
    styleUrls: ["./pricelists-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricelistsPageComponent implements OnInit, OnDestroy {
    @ViewChild("brandCheckForm") brandCheckForm: ContactFormComponent;

    pricelists: PublishedPricelist[] = [];
    filteredPricelists: PublishedPricelist[] = [];
    paginatedPricelists: PublishedPricelist[] = [];
    brandGroups: PreOrderBrandGroup[] = [];
    paginatedBrandGroups: PreOrderBrandGroup[] = [];
    loading = true;
    isLoggedIn = false;
    pageTitle: string = PRICELIST_TEXT.PAGE_TITLE_PRE_ORDER;
    isPreOrderPage = true;
    isPopupOpen = false;
    filtersExpanded = false;
    failedProductGtins: string[] = [];
    moqViolations: Array<{ gtin: string; moq: number; quantity: number }> = [];
    showMovTooltip = false;
    expandedBrands = new Set<string>();

    currentPage = 1;
    totalPages = 1;
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE;

    facetValues = null;
    activeFacetValueIds: string[] = [];

    readonly TEXT = PRICELIST_TEXT;
    readonly ITEMS_PER_PAGE_OPTIONS = ITEMS_PER_PAGE_OPTIONS;
    readonly movFilters = MOV_FILTER_OPTIONS;
    readonly leadTimeFilters = LEAD_TIME_FILTER_OPTIONS;
    brandOptions: string[] = [];
    depositOptions: number[] = [];

    selectedMovFilters = new Set<string>();
    selectedLeadTimeFilters = new Set<string>();
    selectedBrands = new Set<string>();

    draftBrand = "";
    draftMov = "";
    draftEta = "";
    draftDeposit = "";
    brandFilterOpen = false;
    appliedBrand = "";
    appliedMov = "";
    appliedEta = "";
    appliedDeposit = "";
    downloadingBrandReport = false;

    sortKey: PreOrderSortKey | null = null;
    sortDir: "asc" | "desc" = "asc";

    readonly opportunityPageSizes = [6, 12, 24, 48];
    totalSkus: number | null = null;
    metricsLoading = false;
    brandMetrics = new Map<string, PreOrderBrandMetric>();
    offerMetrics = new Map<string, PreOrderBrandMetric>();
    categoryOptions: string[] = [];
    selectedCategories = new Set<string>();
    featuredOnly = false;
    sidebarBrandSearch = "";
    showAllBrands = false;
    opportunityMenu: string | null = null;

    get isMobileOpportunity(): boolean { return typeof window !== "undefined" && window.matchMedia("(max-width:767px)").matches; }
    get sidebarBrands(): string[] { return this.brandOptions.filter(b => b.toLowerCase().includes(this.sidebarBrandSearch.trim().toLowerCase())).slice(0, this.showAllBrands ? undefined : 5); }
    get featuredCount(): number { return this.pricelists.filter(p => p.featured).length; }
    brandOfferCount(brand: string): number { return this.pricelists.filter(p => (p.brands ?? "").split(",").some(b => b.trim().toLowerCase() === brand.toLowerCase())).length; }
    metricForBrand(brand: string): PreOrderBrandMetric | undefined { return this.brandMetrics.get(brand.toLowerCase()); }
    metricForOffer(brand: string, offer: PublishedPricelist): PreOrderBrandMetric | undefined { return this.offerMetrics.get(`${offer.id}:${brand.toLowerCase()}`); }
    categoryCount(category: string): number { return this.brandOptions.filter(brand => this.metricForBrand(brand)?.categories.includes(category)).length; }
    isFeaturedGroup(group: PreOrderBrandGroup): boolean { return group.offers.some(o => o.featured); }
    discountLabel(metric?: PreOrderBrandMetric): string { return metric?.retailDiscountPercent != null ? `up to −${metric.retailDiscountPercent}%` : "—"; }
    discountTooltip(metric?: PreOrderBrandMetric): string { return metric?.matchedSkuCount ? `Highest saving against the lowest EUR retail average across ${metric.matchedSkuCount} matched SKUs.` : "Retail comparison is unavailable."; }
    toggleOpportunityBrand(brand: string): void { this.selectedBrands.has(brand) ? this.selectedBrands.delete(brand) : this.selectedBrands.add(brand); this.applyFilters(); }
    toggleCategory(category: string): void { this.selectedCategories.has(category) ? this.selectedCategories.delete(category) : this.selectedCategories.add(category); this.applyFilters(); }
    toggleFeatured(): void { this.featuredOnly = !this.featuredOnly; this.applyFilters(); }
    setOpportunityMov(value: string): void { this.draftMov = this.appliedMov = value; this.opportunityMenu = null; this.applyFilters(); }
    setOpportunityEta(value: string): void { this.draftEta = this.appliedEta = value; this.opportunityMenu = null; this.applyFilters(); }
    opportunityMovLabel(): string { return this.movFilters.find(f => f.id === this.appliedMov)?.label ?? "Any"; }
    opportunityEtaLabel(): string { return this.leadTimeFilters.find(f => f.id === this.appliedEta)?.label ?? "Any"; }
    clearOpportunityFilters(): void { this.selectedBrands.clear(); this.selectedCategories.clear(); this.featuredOnly = false; this.sidebarBrandSearch = ""; this.clearFilterBar(); }
    get opportunityChips(): Array<{ id: string; label: string }> {
        return [...Array.from(this.selectedBrands).map(brand => ({ id: `brand:${brand}`, label: brand })),
            ...Array.from(this.selectedCategories).map(category => ({ id: `category:${category}`, label: category })),
            ...(this.featuredOnly ? [{ id: "featured", label: "Featured" }] : []),
            ...(this.appliedMov ? [{ id: "mov", label: this.opportunityMovLabel() }] : []),
            ...(this.appliedEta ? [{ id: "eta", label: this.opportunityEtaLabel() }] : [])];
    }
    removeOpportunityChip(id: string): void {
        if (id.startsWith("brand:")) this.selectedBrands.delete(id.slice(6));
        else if (id.startsWith("category:")) this.selectedCategories.delete(id.slice(9));
        else if (id === "featured") this.featuredOnly = false;
        else if (id === "mov") this.draftMov = this.appliedMov = "";
        else if (id === "eta") this.draftEta = this.appliedEta = "";
        this.applyFilters();
    }
    @HostListener("document:keydown.escape")
    closeOpportunityMenus(): void { this.opportunityMenu = null; this.filtersExpanded = false; this.cd.markForCheck(); }
    private loadOpportunityMetrics(): void {
        this.metricsLoading = true;
        this.dataService.query<PreOrderBrandMetricsResponse>(GET_PREORDER_BRAND_METRICS, {}, "network-only").pipe(takeUntil(this.destroy$)).subscribe({
            next: ({ preOrderBrandMetrics }) => {
                this.totalSkus = preOrderBrandMetrics.totalSkuCount;
                this.brandMetrics.clear(); this.offerMetrics.clear();
                for (const metric of preOrderBrandMetrics.items) {
                    if (metric.offerId) this.offerMetrics.set(`${metric.offerId}:${metric.brand.toLowerCase()}`, metric);
                    else this.brandMetrics.set(metric.brand.toLowerCase(), metric);
                }
                const categories = new Set<string>();
                this.brandOptions.forEach(brand => this.metricForBrand(brand)?.categories.forEach(category => categories.add(category)));
                this.categoryOptions = Array.from(categories).sort();
                this.metricsLoading = false; this.applyFilters();
            }, error: () => { this.metricsLoading = false; this.totalSkus = null; this.cd.markForCheck(); }
        });
    }

    private destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private notificationService: NotificationService,
        private exportDownloadService: ExportDownloadService,
        private preorderBrandReportService: PreorderBrandReportService,
        private route: ActivatedRoute,
        private router: Router,
        private cd: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.dataService
            .query(GET_ACTIVE_CUSTOMER)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: { activeCustomer?: { id?: string } | null }) => {
                    this.isLoggedIn = !!res.activeCustomer;
                    this.cd.markForCheck();
                },
            });

        this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const type = params["type"] ?? this.route.snapshot.data["type"];
            this.isPreOrderPage = type === SUPPLY_TYPE.PRE_ORDER;
            this.pageTitle = this.isPreOrderPage
                ? PRICELIST_TEXT.PAGE_TITLE_PRE_ORDER
                : PRICELIST_TEXT.PAGE_TITLE_STOCK;
            this.itemsPerPage = this.isPreOrderPage
                ? 12
                : DEFAULT_ITEMS_PER_PAGE;
            this.loadPricelists();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener("document:click", ["$event"])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest(".preorder-menu")) { this.opportunityMenu = null; this.cd.markForCheck(); }
        if (!target?.closest(".preorder-page__brand-search")) {
            this.closeBrandFilter();
        }
    }

    openBrandFilter = (): void => {
        this.brandFilterOpen = true;
        this.cd.markForCheck();
    };

    closeBrandFilter = (): void => {
        if (!this.brandFilterOpen) {
            return;
        }
        this.brandFilterOpen = false;
        this.cd.markForCheck();
    };

    onBrandFilterInput = (value: string): void => {
        this.draftBrand = value;
        this.brandFilterOpen = true;
        this.cd.markForCheck();
    };

    setDraftMov = (value: string): void => {
        this.draftMov = value;
        this.cd.markForCheck();
    };

    setDraftEta = (value: string): void => {
        this.draftEta = value;
        this.cd.markForCheck();
    };

    setDraftDeposit = (value: string): void => {
        this.draftDeposit = value;
        this.cd.markForCheck();
    };

    selectBrandOption = (brand: string): void => {
        this.draftBrand = brand;
        this.closeBrandFilter();
    };

    get canGoPrevious(): boolean {
        return this.currentPage > 1;
    }

    get canGoNext(): boolean {
        return this.currentPage < this.totalPages;
    }

    get brandCount(): number {
        return this.uniqueBrandNames(this.pricelists).length;
    }

    get activeOfferCount(): number {
        return this.pricelists.length;
    }

    get showingFrom(): number {
        if (this.brandGroups.length === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.itemsPerPage + 1;
    }

    get showingTo(): number {
        return Math.min(
            this.currentPage * this.itemsPerPage,
            this.brandGroups.length,
        );
    }

    get matchText(): string {
        return `${this.brandGroups.length} of ${this.brandCount} ${this.TEXT.MATCH_SUFFIX}`;
    }

    get filteredBrandOptions(): string[] {
        const term = this.draftBrand.trim().toLowerCase();
        if (!term) {
            return this.brandOptions;
        }
        return this.brandOptions.filter((brand) =>
            brand.toLowerCase().includes(term),
        );
    }

    get hasPendingFilterChanges(): boolean {
        return (
            this.draftBrand.trim() !== this.appliedBrand ||
            this.draftMov !== this.appliedMov ||
            this.draftEta !== this.appliedEta ||
            this.draftDeposit !== this.appliedDeposit
        );
    }

    get activeChips(): PreOrderFilterChip[] {
        const chips: PreOrderFilterChip[] = [];
        if (this.appliedBrand) {
            chips.push({
                id: "brand",
                key: this.TEXT.FILTER_BRAND,
                value: this.appliedBrand,
            });
        }
        if (this.appliedMov) {
            const filter = MOV_FILTER_OPTIONS.find((f) => f.id === this.appliedMov);
            chips.push({
                id: "mov",
                key: this.TEXT.FILTER_MOV,
                value: filter?.label ?? this.appliedMov,
            });
        }
        if (this.appliedEta) {
            const filter = LEAD_TIME_FILTER_OPTIONS.find(
                (f) => f.id === this.appliedEta,
            );
            chips.push({
                id: "eta",
                key: this.TEXT.FILTER_ETA,
                value: filter?.label ?? this.appliedEta,
            });
        }
        if (this.appliedDeposit) {
            chips.push({
                id: "deposit",
                key: this.TEXT.FILTER_DEPOSIT,
                value: `${this.appliedDeposit}%`,
            });
        }
        return chips;
    }

    get showGuestUnlock(): boolean {
        return this.isPreOrderPage && !this.isLoggedIn && !this.loading;
    }

    get hasDraftOrAppliedFilters(): boolean {
        return (
            !!this.draftBrand ||
            !!this.draftMov ||
            !!this.draftEta ||
            !!this.draftDeposit ||
            !!this.appliedBrand ||
            !!this.appliedMov ||
            !!this.appliedEta ||
            !!this.appliedDeposit
        );
    }

    goToFirstPage = (): void => {
        this.goToPage(1);
    };

    goToLastPage = (): void => {
        this.goToPage(this.totalPages);
    };

    goToPage = (page: number): void => {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.updatePagination();
            this.cd.markForCheck();
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    goToNextPage = (): void => {
        if (this.canGoNext) {
            this.goToPage(this.currentPage + 1);
        }
    };

    goToPreviousPage = (): void => {
        if (this.canGoPrevious) {
            this.goToPage(this.currentPage - 1);
        }
    };

    getPageNumbers = (): number[] => {
        const pages: number[] = [];
        let start = Math.max(1, this.currentPage - Math.floor(MAX_VISIBLE_PAGES / 2));
        const end = Math.min(this.totalPages, start + MAX_VISIBLE_PAGES - 1);
        start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    goToSignIn = (): void => {
        this.router.navigate(["/account/sign-in"]);
    };

    onViewPricelist = (pricelist: PublishedPricelist): void => {
        if (!this.isLoggedIn) {
            this.goToSignIn();
            return;
        }
        this.router.navigate(["/category/products", pricelist.id]);
    };

    onSharePricelist = (event: {
        pricelist: PublishedPricelist;
        format: ExportFormat;
    }): void => {
        this.copyShareLink(() =>
            this.exportDownloadService.copySupplierShareLink(
                event.pricelist.id,
                event.format,
            ),
        );
    };

    onShareCatalog = (format: ExportFormat): void => {
        const supplyType = this.isPreOrderPage
            ? SUPPLY_TYPE.PRE_ORDER
            : SUPPLY_TYPE.STOCK;

        this.copyShareLink(() =>
            this.exportDownloadService.copyCatalogShareLink(supplyType, format),
        );
    };

    onDownloadPricelist = (event: {
        pricelist: PublishedPricelist;
        format: ExportFormat;
    }): void => {
        if (!this.isLoggedIn) {
            this.goToSignIn();
            return;
        }
        const { pricelist, format } = event;
        const brand = pricelist.brands?.split(",")[0]?.trim() || "Offer";
        const date = new Date().toISOString().slice(0, 10);
        const filenameBase = `${brand} Offer #${pricelist.id} ${date}`;

        this.exportDownloadService
            .downloadSupplierShareable(pricelist.id, filenameBase, format)
            .then(() => {
                this.notificationService
                    .success("Pricelist downloaded successfully")
                    .subscribe();
            })
            .catch((error) => {
                console.error("Error downloading pricelist:", error);
                this.notificationService
                    .error("Error: " + error.message)
                    .subscribe();
            });
    };

    onDownloadOffer = (
        pricelist: PublishedPricelist,
        format: ExportFormat,
    ): void => {
        this.onDownloadPricelist({ pricelist, format });
    };

    onShareOffer = (
        pricelist: PublishedPricelist,
        format: ExportFormat,
    ): void => {
        if (!this.isLoggedIn) {
            this.goToSignIn();
            return;
        }
        this.onSharePricelist({ pricelist, format });
    };

    hasActiveFilters = (): boolean =>
        this.selectedMovFilters.size > 0 ||
        this.selectedLeadTimeFilters.size > 0 ||
        this.selectedBrands.size > 0 ||
        this.activeChips.length > 0;

    trackByPricelist = (_index: number, pricelist: PublishedPricelist): string =>
        pricelist.id;

    trackByBrand = (_index: number, group: PreOrderBrandGroup): string =>
        group.name;

    openFilterModal = (): void => undefined;

    onItemsPerPageChange = (value: number | string): void => {
        this.itemsPerPage = Number(value);
        this.currentPage = 1;
        this.updatePagination();
        this.cd.markForCheck();
    };

    togglePopup = (): void => {
        this.isPopupOpen = !this.isPopupOpen;
    };

    closePopup = (): void => {
        this.isPopupOpen = false;
    };

    toggleFilters = (): void => {
        this.filtersExpanded = !this.filtersExpanded;
        this.cd.markForCheck();
    };

    closeFilters = (): void => {
        this.filtersExpanded = false;
        this.cd.markForCheck();
    };

    toggleMovTooltip = (): void => {
        this.showMovTooltip = !this.showMovTooltip;
        this.cd.markForCheck();
    };

    toggleBrand = (name: string): void => {
        if (this.expandedBrands.has(name)) {
            this.expandedBrands.delete(name);
        } else {
            this.expandedBrands.add(name);
        }
        this.cd.markForCheck();
    };

    isBrandExpanded = (name: string): boolean => this.expandedBrands.has(name);

    isGroupOpen = (name: string, index: number): boolean =>
        this.isBrandExpanded(name) || (this.showGuestUnlock && index === 0);

    sortBy = (key: PreOrderSortKey): void => {
        if (this.sortKey === key) {
            this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
        } else {
            this.sortKey = key;
            this.sortDir = "asc";
        }
        this.applyFilters();
    };

    sortArrow = (key: PreOrderSortKey): string => {
        if (this.sortKey !== key) {
            return "▲▼";
        }
        return this.sortDir === "asc" ? "▲" : "▼";
    };

    applyFilterBar = (): void => {
        this.appliedBrand = this.draftBrand.trim();
        this.appliedMov = this.draftMov;
        this.appliedEta = this.draftEta;
        this.appliedDeposit = this.draftDeposit;
        this.closeBrandFilter();
        this.applyFilters();
    };

    clearFilterBar = (): void => {
        this.draftBrand = "";
        this.draftMov = "";
        this.draftEta = "";
        this.draftDeposit = "";
        this.appliedBrand = "";
        this.appliedMov = "";
        this.appliedEta = "";
        this.appliedDeposit = "";
        this.applyFilters();
    };

    removeChip = (id: string): void => {
        if (id === "brand") {
            this.draftBrand = "";
            this.appliedBrand = "";
        }
        if (id === "mov") {
            this.draftMov = "";
            this.appliedMov = "";
        }
        if (id === "eta") {
            this.draftEta = "";
            this.appliedEta = "";
        }
        if (id === "deposit") {
            this.draftDeposit = "";
            this.appliedDeposit = "";
        }
        this.applyFilters();
    };

    openBrandCheck = (): void => {
        this.brandCheckForm?.openModal();
    };

    formatMov = (mov: number | null | undefined): string => {
        if (mov === null || mov === undefined) {
            return "—";
        }
        return `€${(mov / 100).toLocaleString()}`;
    };

    formatEta = (pricelist: PublishedPricelist): string =>
        this.formatGroupEta({
            name: "",
            offers: [pricelist],
            mov: pricelist.mov,
            leadTimeFrom: pricelist.leadTimeFrom,
            leadTimeTo: pricelist.leadTimeTo,
            preOrderShippingDate: pricelist.preOrderShippingDate,
        });

    formatGroupEta = (group: PreOrderBrandGroup): string => {
        if (group.leadTimeFrom != null || group.leadTimeTo != null) return formatLeadTimeWeeksLabel(group.leadTimeFrom, group.leadTimeTo);
        if (group.preOrderShippingDate) {
            return group.preOrderShippingDate;
        }
        return formatLeadTimeWeeksLabel(group.leadTimeFrom, group.leadTimeTo);
    };

    formatDeposit = (value: number | null | undefined): string => {
        if (value === null || value === undefined) {
            return "—";
        }
        return `${value}%`;
    };

    offerIdLabel = (pricelist: PublishedPricelist): string =>
        `#${pricelist.id}`;

    offerToggleLabel = (group: PreOrderBrandGroup): string => {
        const count = group.offers.length;
        if (this.isBrandExpanded(group.name)) {
            return this.TEXT.HIDE_OFFERS;
        }
        return `${this.TEXT.VIEW_OFFERS} (${count})`;
    };

    downloadAllProducts = (format: ExportFormat): void => {
        const date = new Date().toISOString().slice(0, 10);
        const supplyType = this.isPreOrderPage
            ? SUPPLY_TYPE.PRE_ORDER
            : SUPPLY_TYPE.STOCK;
        const filenameBase = this.isPreOrderPage
            ? `pre-order-catalog-${date}`
            : `stock-catalog-${date}`;

        this.exportDownloadService
            .downloadCatalogShareable(supplyType, filenameBase, format)
            .catch((error) => {
                console.error("Error downloading catalog:", error);
                this.notificationService
                    .error("Error: " + error.message)
                    .subscribe();
            });
    };

    downloadPreorderBrandlists = (): void => {
        if (this.downloadingBrandReport) {
            return;
        }
        this.downloadingBrandReport = true;
        this.cd.markForCheck();
        this.preorderBrandReportService
            .downloadPdfReport(this.pricelists)
            .catch((error) => {
                const message =
                    error instanceof Error ? error.message : String(error);
                this.notificationService.error("Error: " + message).subscribe();
            })
            .finally(() => {
                this.downloadingBrandReport = false;
                this.cd.markForCheck();
            });
    };

    private copyShareLink = (copyFn: () => Promise<void>): void => {
        copyFn()
            .then(() => {
                this.notificationService.success(SHARE_LINK_SUCCESS).subscribe();
            })
            .catch((error) => {
                this.notificationService
                    .error(SHARE_LINK_ERROR_PREFIX + error.message)
                    .subscribe();
            });
    };

    private loadPricelists = (): void => {
        this.loading = true;
        this.cd.markForCheck();
        const supplyType = this.isPreOrderPage
            ? SUPPLY_TYPE.PRE_ORDER
            : SUPPLY_TYPE.STOCK;

        this.dataService
            .query<GetPublishedPricelistsResponse>(GET_PUBLISHED_PRICELISTS, {
                supplyType,
            }, "network-only")
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.pricelists = result.publishedPricelists ?? [];
                    if (this.isPreOrderPage) this.loadOpportunityMetrics();
                    this.extractBrandOptions();
                    this.extractDepositOptions();
                    this.applyFilters();
                    this.loading = false;
                    this.cd.markForCheck();
                },
                error: () => {
                    this.pricelists = [];
                    this.extractBrandOptions();
                    this.extractDepositOptions();
                    this.applyFilters();
                    this.loading = false;
                    this.cd.markForCheck();
                },
            });
    };

    private extractBrandOptions = (): void => {
        this.brandOptions = this.uniqueBrandNames(this.pricelists);
    };

    private extractDepositOptions = (): void => {
        const deposits = new Set<number>();
        this.pricelists.forEach((p) => {
            if (p.prePaymentPercentage != null) {
                deposits.add(p.prePaymentPercentage);
            }
        });
        this.depositOptions = Array.from(deposits).sort((a, b) => a - b);
    };

    private uniqueBrandNames = (list: PublishedPricelist[]): string[] => {
        const brands = new Set<string>();
        list.forEach((p) => {
            p.brands?.split(",").forEach((b) => {
                const trimmed = b.trim();
                if (trimmed) {
                    brands.add(trimmed);
                }
            });
        });
        return Array.from(brands).sort((a, b) => a.localeCompare(b));
    };

    toggleMovFilter = (filterId: string): void => {
        if (this.selectedMovFilters.has(filterId)) {
            this.selectedMovFilters.delete(filterId);
        } else {
            this.selectedMovFilters.add(filterId);
        }
        this.applyFilters();
    };

    toggleLeadTimeFilter = (filterId: string): void => {
        if (this.selectedLeadTimeFilters.has(filterId)) {
            this.selectedLeadTimeFilters.delete(filterId);
        } else {
            this.selectedLeadTimeFilters.add(filterId);
        }
        this.applyFilters();
    };

    toggleBrandFilter = (brand: string): void => {
        if (this.selectedBrands.has(brand)) {
            this.selectedBrands.delete(brand);
        } else {
            this.selectedBrands.add(brand);
        }
        this.applyFilters();
    };

    clearFilters = (): void => {
        this.selectedMovFilters.clear();
        this.selectedLeadTimeFilters.clear();
        this.selectedBrands.clear();
        this.clearFilterBar();
    };

    private applyFilters = (): void => {
        let result = [...this.pricelists];

        if (this.isPreOrderPage) {
            if (this.selectedBrands.size) result = result.filter(p => (p.brands ?? "").split(",").some(b => this.selectedBrands.has(b.trim())));
            if (this.featuredOnly) result = result.filter(p => p.featured);
            if (this.selectedCategories.size) result = result.filter(p => (p.brands ?? "").split(",").some(b => this.metricForBrand(b.trim())?.categories.some(c => this.selectedCategories.has(c))));
            if (this.appliedBrand) {
                result = result.filter((p) =>
                    this.pricelistMatchesBrandSearch(p, this.appliedBrand),
                );
            }
            if (this.appliedMov) {
                const filter = MOV_FILTER_OPTIONS.find((f) => f.id === this.appliedMov);
                if (filter) {
                    result = result.filter(
                        (p) => p.mov != null && p.mov <= filter.max,
                    );
                }
            }
            if (this.appliedEta) {
                const filter = LEAD_TIME_FILTER_OPTIONS.find(
                    (f) => f.id === this.appliedEta,
                );
                if (filter) {
                    result = result.filter((p) => {
                        const leadTime = resolveEffectiveLeadTimeWeeks(
                            p.leadTimeFrom,
                            p.leadTimeTo,
                        );
                        if (leadTime == null) {
                            return false;
                        }
                        return filter.id === LEAD_TIME_FILTER_IDS.MORE_THAN_SIX_WEEKS ? leadTime > 6 : leadTime <= (filter.max ?? Infinity);
                    });
                }
            }
            if (this.appliedDeposit) {
                const deposit = Number(this.appliedDeposit);
                result = result.filter((p) => p.prePaymentPercentage === deposit);
            }
        } else {
            if (this.selectedMovFilters.size > 0) {
                result = result.filter((p) => {
                    const mov = p.mov ?? 0;
                    return Array.from(this.selectedMovFilters).some((filterId) => {
                        const filter = MOV_FILTER_OPTIONS.find((f) => f.id === filterId);
                        if (!filter) return false;
                        return mov <= filter.max;
                    });
                });
            }

            if (this.selectedLeadTimeFilters.size > 0) {
                result = result.filter((p) => {
                    const leadTime =
                        resolveEffectiveLeadTimeWeeks(
                            p.leadTimeFrom,
                            p.leadTimeTo,
                        ) ?? 0;
                    return Array.from(this.selectedLeadTimeFilters).some((filterId) => {
                        const filter = LEAD_TIME_FILTER_OPTIONS.find((f) => f.id === filterId);
                        if (!filter) return false;
                        if (filterId === LEAD_TIME_FILTER_IDS.MORE_THAN_SIX_WEEKS) {
                            return leadTime > 6;
                        }
                        return leadTime <= (filter.max ?? Infinity);
                    });
                });
            }

            if (this.selectedBrands.size > 0) {
                result = result.filter((p) => {
                    if (!p.brands) return false;
                    const pricelistBrands = p.brands.split(",").map((b) => b.trim());
                    return Array.from(this.selectedBrands).some((brand) =>
                        pricelistBrands.includes(brand)
                    );
                });
            }
        }

        this.filteredPricelists = result;
        if (this.isPreOrderPage) {
            this.brandGroups = this.sortBrandGroups(this.buildBrandGroups(result).filter(group =>
                (!this.selectedBrands.size || this.selectedBrands.has(group.name)) &&
                (!this.appliedBrand || group.name.toLowerCase().includes(this.appliedBrand.toLowerCase())) &&
                (!this.selectedCategories.size || this.metricForBrand(group.name)?.categories.some(category => this.selectedCategories.has(category)))
            ));
            this.syncExpandedBrands();
        }
        this.currentPage = 1;
        this.updatePagination();
        this.cd.markForCheck();
    };

    private pricelistMatchesBrandSearch = (
        pricelist: PublishedPricelist,
        search: string,
    ): boolean => {
        if (!pricelist.brands) {
            return false;
        }
        const term = search.trim().toLowerCase();
        if (!term) {
            return false;
        }
        return pricelist.brands
            .split(",")
            .map((b) => b.trim())
            .some((b) => b.toLowerCase().includes(term));
    };

    private buildBrandGroups = (
        list: PublishedPricelist[],
    ): PreOrderBrandGroup[] => {
        const map = new Map<string, PublishedPricelist[]>();
        list.forEach((pricelist) => {
            const names = (pricelist.brands || "")
                .split(",")
                .map((b) => b.trim())
                .filter(Boolean);
            if (names.length === 0) {
                names.push("Other");
            }
            names.forEach((name) => {
                const offers = map.get(name) ?? [];
                offers.push(pricelist);
                map.set(name, offers);
            });
        });

        return Array.from(map.entries()).map(([name, offers]) => {
            const movs = offers
                .map((o) => o.mov)
                .filter((m): m is number => m != null && Number.isFinite(m));
            const effectiveWeeks = offers
                .map((o) => pricingLeadTimeToFormFields(o.leadTimeFrom, o.leadTimeTo).fromAmount)
                .filter((v): v is number => v != null);
            const dates = offers
                .map((o) => o.preOrderShippingDate)
                .filter((d): d is string => !!d);

            return {
                name,
                offers,
                mov: movs.length ? Math.min(...movs) : null,
                leadTimeFrom: effectiveWeeks.length
                    ? Math.min(...effectiveWeeks)
                    : null,
                leadTimeTo: effectiveWeeks.length
                    ? Math.max(...offers.map(o => pricingLeadTimeToFormFields(o.leadTimeFrom, o.leadTimeTo).toAmount).filter((v): v is number => v != null))
                    : null,
                preOrderShippingDate: dates[0] ?? null,
            };
        });
    };

    private sortBrandGroups = (
        groups: PreOrderBrandGroup[],
    ): PreOrderBrandGroup[] => {
        const sorted = [...groups];
        const dir = this.sortDir === "desc" ? -1 : 1;
        sorted.sort((a, b) => {
            if (!this.sortKey) return Number(this.isFeaturedGroup(b)) - Number(this.isFeaturedGroup(a)) || a.name.localeCompare(b.name);
            if (this.sortKey === "skus") return ((this.metricForBrand(a.name)?.skuCount ?? 0) - (this.metricForBrand(b.name)?.skuCount ?? 0)) * dir;
            if (this.sortKey === "retail") return ((this.metricForBrand(a.name)?.retailDiscountPercent ?? 0) - (this.metricForBrand(b.name)?.retailDiscountPercent ?? 0)) * dir;
            if (this.sortKey === "brand") {
                return a.name.localeCompare(b.name) * (this.sortKey ? dir : 1);
            }
            if (this.sortKey === "mov") {
                return ((a.mov ?? 0) - (b.mov ?? 0)) * dir;
            }
            const aEta =
                resolveEffectiveLeadTimeWeeks(a.leadTimeFrom, a.leadTimeTo) ??
                0;
            const bEta =
                resolveEffectiveLeadTimeWeeks(b.leadTimeFrom, b.leadTimeTo) ??
                0;
            return (aEta - bEta) * dir;
        });
        return sorted;
    };

    private syncExpandedBrands = (): void => {
        if (this.brandGroups.length === 0) {
            this.expandedBrands.clear();
            return;
        }
        const names = new Set(this.brandGroups.map((g) => g.name));
        Array.from(this.expandedBrands).forEach((name) => {
            if (!names.has(name)) {
                this.expandedBrands.delete(name);
            }
        });
        if (this.expandedBrands.size === 0 && !this.isMobileOpportunity) {
            this.expandedBrands.add(this.brandGroups[0].name);
        }
    };

    private updatePagination = (): void => {
        if (this.isPreOrderPage) {
            this.totalPages =
                Math.ceil(this.brandGroups.length / this.itemsPerPage) || 1;
            const startIndex = (this.currentPage - 1) * this.itemsPerPage;
            const endIndex = startIndex + this.itemsPerPage;
            this.paginatedBrandGroups = this.brandGroups.slice(startIndex, endIndex);
            return;
        }
        this.totalPages = Math.ceil(this.filteredPricelists.length / this.itemsPerPage) || 1;
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.paginatedPricelists = this.filteredPricelists.slice(startIndex, endIndex);
    };
}
